import { fetchShopAccounts } from 'admin/actions/shops';
import Tooltip from 'admin/components/atoms/Tooltip';
import ShopSelector from 'admin/components/organisms/ShopSelector';
import Button from 'admin/components/shared/Button';
import { ensureArray } from 'admin/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Col, Form, FormGroup, Input, Label } from 'reactstrap';
import styled from 'styled-components';
import { useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import LocalizedMask from '../../atoms/LocalizedMask';
import Required from '../../atoms/Required';
import TextArea from '../../atoms/TextArea';

const MyShopSelector = styled(ShopSelector)``;

const GrayoutMessage = styled.div`
  color: #555;
`;

const AdditionalPrivateMoneySelector = ({ shop, privateMoneys, register }) => {
  if (!shop.id) {
    return <GrayoutMessage>店舗を選択してください</GrayoutMessage>;
  }

  if (privateMoneys.length === 0) {
    return <GrayoutMessage>選択肢なし</GrayoutMessage>;
  }

  const { ref: additionalPrivateMoneyIdsRef, ...additionalPrivateMoneyIdsRegisterProps } = register(
    'additional_private_money_ids'
  );

  return (
    <FormGroup check>
      {privateMoneys.map((p) => (
        <div key={`additional_private_money_${p.id}`}>
          <Label check>
            <Input
              type="checkbox"
              innerRef={additionalPrivateMoneyIdsRef}
              {...additionalPrivateMoneyIdsRegisterProps}
              value={p.id}
            />
            {p.name}
          </Label>
        </div>
      ))}
    </FormGroup>
  );
};

AdditionalPrivateMoneySelector.propTypes = {
  shop: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  privateMoneys: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  register: PropTypes.func,
};

AdditionalPrivateMoneySelector.defaultProps = {
  register: null,
};

const BillForms = ({ onSubmit }) => {
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const { register, control, getValues, resetField } = useForm({
    defaultValues: {
      amount: 0,
    },
  });

  const { ref: descriptionRef, ...descriptionRegisterProps } = register('description');

  const [additionalPrivateMoneys, setAdditionalPrivateMoneys] = useState([]);
  const [shop, setShop] = useState({});
  const [isAmountDisabled, setIsAmountDisabled] = useState(true);

  const onChangeShop = (selectedShop) => {
    setShop(selectedShop || {});
    setAdditionalPrivateMoneys([]);
    resetField('additional_private_money_ids');
  };

  useEffect(() => {
    onChangeShop(null);
  }, [userPrivateMoney]);

  useEffect(() => {
    if (shop.id) {
      fetchShopAccounts(shop.id, 'per_page=100').then((accounts) =>
        setAdditionalPrivateMoneys(
          accounts
            .filter((a) => !a.is_suspended)
            .filter((a) => a.money_name !== userPrivateMoney.name)
            .filter((a) => a.private_money.organization?.code !== 'pocketchange')
            .map((a) => a.private_money)
        )
      );
    } else {
      setAdditionalPrivateMoneys([]);
    }
  }, [shop]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      const { amount, description, additional_private_money_ids } = getValues();
      onSubmit({
        private_money_id: userPrivateMoney.id,
        private_money_name: userPrivateMoney.name,
        user_id: shop.id,
        user_name: shop.name,
        amount: isAmountDisabled ? null : amount,
        description,
        additional_private_moneys: ensureArray(additional_private_money_ids || [])
          .map((id) => additionalPrivateMoneys.find((x) => x.id === id))
          .filter((x) => !!x),
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup row>
        <Label sm={2}>
          発行店舗 <Required />
        </Label>
        <Col sm={4}>
          <div styleName="form-item">
            <MyShopSelector designatedMoney={userPrivateMoney} onChangeShop={onChangeShop} />
          </div>
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label sm={2}>追加対応マネー</Label>
        <Col sm={4}>
          <AdditionalPrivateMoneySelector
            shop={shop}
            privateMoneys={additionalPrivateMoneys}
            register={register}
          />
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label sm={2}>支払い金額</Label>
        <Col sm={3} xl={2} className="align-items-center">
          <FormGroup check>
            <Input
              name="use_amount"
              type="radio"
              checked={isAmountDisabled}
              onChange={() => setIsAmountDisabled(true)}
            />
            <Label check>金額はお客様が入力</Label>
          </FormGroup>
          <FormGroup check>
            <Input
              name="use_amount"
              type="radio"
              checked={!isAmountDisabled}
              onChange={() => setIsAmountDisabled(false)}
            />
            <Label check>金額は固定</Label>
          </FormGroup>
          <FormGroup check className="mt-2">
            <Controller
              control={control}
              name="amount"
              render={({ field: { onChange, value } }) => (
                <LocalizedMask value={isAmountDisabled ? '' : value} onChange={onChange}>
                  {(inputProps) => (
                    <Input {...inputProps} id="amount" disabled={isAmountDisabled} />
                  )}
                </LocalizedMask>
              )}
            />
          </FormGroup>
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label sm={2}>
          説明文
          <Tooltip
            id="helpDescription"
            text={
              <div>
                QRコードと共にお客様に表示するメッセージ（200文字まで）です。
                <br />
                取引詳細の説明文にも表示します。
              </div>
            }
          />
        </Label>
        <Col sm={6}>
          <TextArea
            fullWidth
            type="text"
            id="description"
            maxLength={200}
            innerRef={descriptionRef}
            {...descriptionRegisterProps}
          />
        </Col>
      </FormGroup>
      <Button type="submit">発行</Button>
    </Form>
  );
};

BillForms.propTypes = {
  onSubmit: PropTypes.func,
};

BillForms.defaultProps = {
  onSubmit: null,
};

export default BillForms;
